// frontend/src/utils/sstoTimezone.ts
// Единый источник истины для выбранного timezone (AppBar ↔ Form)

export type SstoTimezone = 'local' | 'moscow' | 'utc';

const STORAGE_KEY = 'ssto_timezone';
const EVENT_NAME = 'ssto:timezone-changed';

export function getStoredTimezone(): SstoTimezone {
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === 'local' || v === 'moscow' || v === 'utc') return v;
  return 'local';
}

export function setStoredTimezone(tz: SstoTimezone): void {
  localStorage.setItem(STORAGE_KEY, tz);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: tz }));
}

export function subscribeTimezone(cb: (tz: SstoTimezone) => void): () => void {
  const onCustom = (e: Event) => {
    cb((e as CustomEvent<SstoTimezone>).detail);
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY && e.newValue) {
      cb(e.newValue as SstoTimezone);
    }
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}

/** Timezone label for form fields */
export function getTimezoneLabel(tz: SstoTimezone): string {
  switch (tz) {
    case 'local':
      return 'Местное время';
    case 'moscow':
      return 'Московское время';
    case 'utc':
      return 'Время по Гринвичу';
  }
}

/** Parse datetime-local input deterministically */
function parseDateTimeLocal(input: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(input);
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4]),
    minute: Number(m[5]),
  };
}

/** Convert datetime-local string to UTC ISO depending on timezone */
export function toUtcIso(input: string, tz: SstoTimezone): string | null {
  if (!input) return null;

  if (tz === 'local') {
    const d = new Date(input);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }

  const parts = parseDateTimeLocal(input);
  if (!parts) return null;

  const utcMs =
    tz === 'utc'
      ? Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute)
      : Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour - 3, parts.minute);

  const d = new Date(utcMs);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
